// 決算書作成ページ (2026-06-01 layout 駆動 / Excel 完全踏襲) — freee 自動取得 → 5帳票 → 印刷/PDF
//   会社独自仕様: financialLayout.ts の群定義(現金計=8件 等)に従う(freee 表示名グループとは別)。
//   データ源: api/reports.ts (worker /api/freee-proxy 経由・GET読取のみ・書込なし=R1-R3/R10非抵触)
//   体裁(Excel 実測): MS Mincho・本文12pt/タイトル22pt・全セル細罫線・ヘッダ #CCCCCC・
//     小計/合計 #D9D9D9・数値 #,##0(右寄せ)・構成比 0.00・列幅(A広/数値固定)・群間空行・
//     社名左/印刷日時右・【税抜】【整理仕訳…】(単位:円)・対比表は13列A4横。
//   絵文字禁止 / react-icons/md のみ / @freee_jp/vibes 準拠

import React, { useState, useCallback, useEffect } from 'react';
import {
  Container, PageTitle, CardBase, HStack, Button, Text, Note,
  Breadcrumbs, MessageBlock, InlineSpinner,
} from '@freee_jp/vibes';
import { MdSummarize, MdRefresh, MdPrint, MdWarning, MdVisibility, MdFunctions, MdSubdirectoryArrowRight, MdRemove, MdDownload } from 'react-icons/md';
import {
  ReportKind, REPORT_KINDS, FinancialReport, PeriodSelection,
  fiscalMonthToCalendar,
} from '../types/reports';
import { fetchReport } from '../api/reports';
import { getIncludeAdjustment, setIncludeAdjustment } from '../api/adjustmentMode';
import {
  STYLE, COLUMN_WIDTHS_TRIAL, COLUMN_WIDTHS_SUB, COLUMN_WIDTHS_YOY, FINANCIAL_LAYOUTS,
} from '../data/financialLayout';

const TERMS = [80, 79, 78];
const IMPLEMENTED: ReportKind[] = ['bs', 'pl', 'pl_prev', 'yoy', 'sub_breakdown'];
// 全帳票ZIP一括DL: worker(Browser Rendering)が各帳票を印刷同一CSSで page.pdf 化→ZIP。
const ZIP_API_BASE = 'https://invoice-review.system-0f7.workers.dev';

// --- 数値書式(Excel: #,##0 / 構成比 0.00。マイナスは先頭 -) ---
const fmtCur = (v: number | null): string => {
  if (v == null) return '';
  return Math.round(v).toLocaleString('ja-JP'); // #,##0
};
const fmtRatio = (v: number | null): string => {
  if (v == null) return '';
  return v.toFixed(2); // 0.00
};

// Excel 文字単位 → px 概算(MS Mincho 12pt ≒ 7px/char + padding)
const chW = (ex: number) => `${Math.round(ex * 7 + 6)}px`;

// 明朝Webフォントを先頭に。ローカル(MS明朝/游明朝)が無い環境(Cloudflare Browser Rendering=Linux
// headless Chrome)でも Noto Serif JP を @font-face で読込→ネイティブ日本語明朝で描画・PDF埋込される。
// これで 画面 / 単票印刷(window.print) / ZIP一括DL(worker) の全経路が同一の明朝で統一される。
const FONT = `"Noto Serif JP","MS Mincho","ＭＳ 明朝","Yu Mincho",serif`;
const BORDER = '1px solid #000';
const HEADER_FILL = `#${STYLE.fill.header}`;     // CCCCCC
const SUBTOTAL_FILL = `#${STYLE.fill.subtotal}`; // D9D9D9
const NEW_FILL = '#fee2e2';    // 新規勘定科目の行色(薄赤)
const NEW_DOT = '#dc2626';     // 新規マーカー(赤丸)

const S = {
  page: { background: '#f3f1ef', minHeight: '100vh', paddingBottom: '3rem' } as React.CSSProperties,
  sheet: {
    // padding は印刷の @page margin(5mm 8mm)と一致させる。これで画面プレビューのコンテンツ幅 = 印刷の用紙内容幅となり、
    // content-fit な対比表(13列)も含め screen と print の列幅が揃う(WYSIWYG)。印刷時は PRINT_CSS が .fin-sheet padding を 0 に
    // リセットし @page margin が同じ余白を与えるため、画面と印刷で見た目が一致する。
    background: '#fff', margin: '0 auto', padding: '5mm 8mm',
    boxShadow: '0 1px 4px rgba(0,0,0,0.12)', fontFamily: FONT, color: '#000',
  } as React.CSSProperties,
  title: { textAlign: 'center', fontSize: '22pt', fontWeight: 400, fontFamily: FONT, lineHeight: 1.2 } as React.CSSProperties,
  period: { textAlign: 'center', fontSize: '12pt', fontFamily: FONT, marginTop: 4 } as React.CSSProperties,
  hdrRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12pt', fontFamily: FONT, marginTop: 6 } as React.CSSProperties,
  noteRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12pt', fontFamily: FONT, marginTop: 2 } as React.CSSProperties,
  printedAt: { fontSize: '10pt', fontFamily: FONT } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontFamily: FONT, fontSize: '12pt', marginTop: 10 } as React.CSSProperties,
  cellBase: { border: BORDER, padding: '1px 6px', height: `${STYLE.rowHeight}pt`, whiteSpace: 'nowrap', overflow: 'hidden' } as React.CSSProperties,
};

// ヘッダセル
const thName = (w: string): React.CSSProperties => ({ ...S.cellBase, width: w, background: HEADER_FILL, textAlign: 'left', fontWeight: 400 });
const thNum = (w: string): React.CSSProperties => ({ ...S.cellBase, width: w, background: HEADER_FILL, textAlign: 'center', fontWeight: 400 });

const SEL: React.CSSProperties = { padding: '5px 8px', border: '1px solid #d4d2d0', borderRadius: 4 };

function ReportHeaderView({ r }: { r: FinancialReport }) {
  return (
    <div>
      <div style={S.title}>{r.header.title}</div>
      <div style={S.period}>{r.header.periodLabel}</div>
      <div style={S.hdrRow}>
        <span>{r.header.companyName}</span>
        <span style={S.printedAt}>{r.header.printedAt}</span>
      </div>
      <div style={S.noteRow}>
        <span>{r.header.taxNote}</span>
        <span>{r.header.unitNote}</span>
      </div>
      <div style={S.noteRow}>
        <span>{r.header.adjustNote}</span>
        {r.header.sectionNote ? <span>{r.header.sectionNote}</span> : <span />}
      </div>
    </div>
  );
}

// --- 本表(BS/PL/前期PL/補助) ---
function TrialTableView({ r, newLabels }: { r: FinancialReport; newLabels?: Set<string> }) {
  const isSub = r.kind === 'sub_breakdown';
  const widths = isSub
    ? [COLUMN_WIDTHS_SUB.A, COLUMN_WIDTHS_SUB.B, COLUMN_WIDTHS_SUB.C, COLUMN_WIDTHS_SUB.D, COLUMN_WIDTHS_SUB.E]
    : [COLUMN_WIDTHS_TRIAL.A, COLUMN_WIDTHS_TRIAL.B, COLUMN_WIDTHS_TRIAL.C, COLUMN_WIDTHS_TRIAL.D, COLUMN_WIDTHS_TRIAL.E, COLUMN_WIDTHS_TRIAL.F];

  return (
    <table style={S.table}>
      <colgroup>
        {widths.map((w, i) => (<col key={i} style={{ width: chW(w) }} />))}
      </colgroup>
      <thead>
        <tr>
          <th style={thName(chW(widths[0]))}>科目名</th>
          {r.columns.slice(1).map((c, i) => (<th key={c.key} style={thNum(chW(widths[i + 1]))}>{c.label}</th>))}
        </tr>
      </thead>
      <tbody>
        {r.rows.map((row, i) => {
          if (row.isBlank) {
            // 群間空行: 罫線なしの空行(Excel の空行踏襲)
            return (
              <tr key={i}>
                <td style={{ height: `${STYLE.rowHeight}pt`, border: 'none' }} colSpan={r.columns.length} />
              </tr>
            );
          }
          const filled = row.isSubtotal || row.isTotal;
          const isNew = !!newLabels && !!row.label && newLabels.has(row.label);
          const fill = isNew ? NEW_FILL : (filled ? SUBTOTAL_FILL : undefined);
          const nameStyle: React.CSSProperties = {
            ...S.cellBase, textAlign: 'left', fontWeight: 400,
            paddingLeft: `${6 + row.indent * 12}px`,
            background: fill,
          };
          return (
            <tr key={i}>
              <td style={nameStyle}>
                {isNew && <span style={{ color: NEW_DOT, marginRight: 4, fontSize: '0.7em', verticalAlign: 'middle' }}>●</span>}
                {row.label}
              </td>
              {row.values.map((v, j) => {
                const fmt = r.columns[j + 1].fmt;
                return (
                  <td key={j} style={{ ...S.cellBase, textAlign: 'right', background: fill }}>
                    {fmt === 'ratio' ? fmtRatio(v) : fmt === 'text' ? (v ?? '') : fmtCur(v)}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// --- 科目別前年同月対比表(13列・2段ヘッダ・A4横) ---
function YoyTableView({ r, newLabels }: { r: FinancialReport; newLabels?: Set<string> }) {
  const w = COLUMN_WIDTHS_YOY;
  const exW = [w.A, w.B, w.C, w.D, w.E, w.F, w.G, w.H, w.I, w.J, w.K, w.L, w.M];
  const exTotal = exW.reduce((a, b) => a + b, 0);
  // px 絶対幅(13列計≈1160px)は A4横の内容幅(≈1047px)を超え UI 横はみ出し・印刷右見切れの原因。
  // Excel 列幅比を保ったまま % 配分し、table-layout:fixed + width:100% で常に容器(用紙/画面)内に収める。
  const widthsPct = exW.map((x) => `${((x / exTotal) * 100).toFixed(3)}%`);
  const yoyCell: React.CSSProperties = { ...S.cellBase, padding: '1px 3px' };
  const groups = r.columnGroups || [];
  return (
    <table style={{ ...S.table, fontSize: '9pt' }}>
      <colgroup>
        {widthsPct.map((cw, i) => (<col key={i} style={{ width: cw }} />))}
      </colgroup>
      <thead>
        {/* 1段目: 科目名(縦結合) / 発生 / 残高 */}
        <tr>
          <th rowSpan={2} style={thName(widthsPct[0])}>科目名</th>
          {groups.filter((g) => g.span > 1).map((g, gi) => (
            <th key={gi} colSpan={g.span} style={{ ...yoyCell, background: HEADER_FILL, textAlign: 'center', fontWeight: 400 }}>{g.label}</th>
          ))}
        </tr>
        {/* 2段目: 当期/構成比/前期/構成比/増減額/構成比 ×2 */}
        <tr>
          {r.columns.slice(1).map((c, i) => (<th key={c.key} style={thNum(widthsPct[i + 1])}>{c.label}</th>))}
        </tr>
      </thead>
      <tbody>
        {r.rows.map((row, i) => {
          if (row.isBlank) {
            return (<tr key={i}><td style={{ height: `${STYLE.rowHeight}pt`, border: 'none' }} colSpan={13} /></tr>);
          }
          const filled = row.isSubtotal || row.isTotal;
          const isNew = !!newLabels && !!row.label && newLabels.has(row.label);
          const fill = isNew ? NEW_FILL : (filled ? SUBTOTAL_FILL : undefined);
          return (
            <tr key={i}>
              <td style={{ ...yoyCell, textAlign: 'left', paddingLeft: `${6 + row.indent * 10}px`, background: fill }}>
                {isNew && <span style={{ color: NEW_DOT, marginRight: 4, fontSize: '0.7em', verticalAlign: 'middle' }}>●</span>}
                {row.label}
              </td>
              {row.values.map((v, j) => (
                <td key={j} style={{ ...yoyCell, textAlign: 'right', background: fill }}>
                  {r.columns[j + 1].fmt === 'ratio' ? fmtRatio(v) : fmtCur(v)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// --- 1帳票のシート(単票表示・一括印刷の双方で再利用) ---
function ReportSheet({ r, pageBreak, newLabels }: { r: FinancialReport; pageBreak?: boolean; newLabels?: Set<string> }) {
  const landscape = r.kind === 'yoy' || !!FINANCIAL_LAYOUTS[r.kind]?.landscape;
  const maxW = landscape ? '297mm' : '210mm';
  const breakStyle: React.CSSProperties = pageBreak ? { breakBefore: 'page', pageBreakBefore: 'always' } : {};
  return (
    <div className={`fin-sheet${landscape ? ' print-landscape' : ''}`}
      style={{ ...S.sheet, maxWidth: maxW, overflowX: 'auto', ...breakStyle }}>
      <ReportHeaderView r={r} />
      {r.meta.empty
        ? <div style={{ padding: 24 }} className="no-print">
            <MessageBlock success={false}>この期間のデータが freee から取得できませんでした（空応答）。</MessageBlock>
          </div>
        : (r.kind === 'yoy' ? <YoyTableView r={r} newLabels={newLabels} /> : <TrialTableView r={r} newLabels={newLabels} />)}
    </div>
  );
}

// --- ロバスト化 警告(未分類のみ。新規=自動挿入は通常行として表示し注記しない) ---
function RobustnessNotices({ r }: { r: FinancialReport }) {
  const { unmapped } = r.meta;
  if (!unmapped?.length) return null;
  return (
    <div className="no-print" style={{ marginTop: 12, fontSize: '0.8rem' }}>
      <div style={{ marginBottom: 8 }}>
        <MessageBlock success={false}>
          <HStack gap={1} alignItems="center"><MdWarning /> <Text weight="bold">群を判定できなかった勘定科目（その他(未分類)に分類）</Text></HStack>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {unmapped.map((u, i) => (<li key={i}>{u.accountName}（freee表示名: {u.categoryName} / 金額 {u.amount.toLocaleString('ja-JP')}）</li>))}
          </ul>
        </MessageBlock>
      </div>
    </div>
  );
}

// SHEET_CSS: プレビュー(screen)と印刷(print)で帳票の「書式」を完全一致(WYSIWYG)させるため、表のレイアウト
//   (table-layout/列幅/フォント/余白/折返し)を @media を付けず常時適用する。!important + 高詳細度で React inline
//   (colgroup chW / S.cellBase)を上書きするので、画面でも印刷と同じ列幅・余白・折返しになる。
//   印刷専用の機構(@page / #print-area 配置 / .vb-container static / シート余白リセット / 改ページ / thead 繰返し)のみ PRINT_CSS。
//   ※ 縦本表: 金額4列を均一(table-layout:fixed + 等幅%)。科目名27%/金額16%×4/構成比9%、12pt、数値ゆとり padding8px、
//     科目名は長名のみ折返し(white-space:normal)・数値 nowrap。font12pt は「均一金額+全科目名1行+ゆとり」を満たす A4縦上限(実測)。
//   ※ 横長(対比表13列): table-layout:auto で内容幅、10pt。
const SHEET_CSS = `
/* 日本語明朝(Noto Serif JP)。Google Fonts版は可変フォントで Chrome printToPDF が実体を埋め込まない
   (descriptorのみ書き出し=未埋め込み) ため、 静的(non-variable) woff2 を配る fontsource 版を使用。
   静的サブセットなら Chrome が FontFile としてPDFに subset 埋め込みする。 */
@import url('https://cdn.jsdelivr.net/npm/@fontsource/noto-serif-jp@5/400.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/noto-serif-jp@5/700.css');
.fin-sheet:not(.print-landscape) table { table-layout: fixed !important; width: 100% !important; font-size: 12pt !important; }
.fin-sheet:not(.print-landscape) table th, .fin-sheet:not(.print-landscape) table td { padding-top: 1px !important; padding-bottom: 1px !important; padding-right: 8px !important; white-space: nowrap !important; overflow: hidden !important; }
.fin-sheet:not(.print-landscape) table th:not(:first-child), .fin-sheet:not(.print-landscape) table td:not(:first-child) { padding-left: 8px !important; }
.fin-sheet:not(.print-landscape) table td:first-child, .fin-sheet:not(.print-landscape) table th:first-child { white-space: normal !important; word-break: break-all !important; }
.fin-sheet:not(.print-landscape) table col:nth-child(1) { width: 27% !important; }
.fin-sheet:not(.print-landscape) table col:nth-child(2), .fin-sheet:not(.print-landscape) table col:nth-child(3), .fin-sheet:not(.print-landscape) table col:nth-child(4), .fin-sheet:not(.print-landscape) table col:nth-child(5) { width: 16% !important; }
.fin-sheet:not(.print-landscape) table col:nth-child(6) { width: 9% !important; }
.fin-sheet.print-landscape table { table-layout: auto !important; width: 100% !important; font-size: 10pt !important; }
.fin-sheet.print-landscape table col { width: auto !important; }
.fin-sheet.print-landscape table th, .fin-sheet.print-landscape table td { padding-top: 1px !important; padding-bottom: 1px !important; padding-right: 4px !important; white-space: nowrap !important; }
.fin-sheet.print-landscape table th:not(:first-child), .fin-sheet.print-landscape table td:not(:first-child) { padding-left: 4px !important; width: 1% !important; }
`;

const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #print-area, #print-area * { visibility: visible; }
  /* #3 上余白の主因対策: #print-area の position:absolute の基準が vibes Container(.vb-container, position:relative)で、
     その上の nav/見出し/操作列(visibility:hidden だが領域は占有)分(約185px≈49mm)だけ下がるのが大余白の正体。
     印刷時は Container を static 化して #print-area を用紙基準(top:0)に置く + 画面用 marginTop:16 もリセット。 */
  .vb-container { position: static !important; }
  #print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0 !important; padding: 0 !important; }
  .no-print { display: none !important; }
  .fin-sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important; max-width: none !important; width: 100% !important; }
  /* #3 先頭シート/ヘッダの上余白も除去(用紙上端の @page margin のみ効かせる) */
  #print-area .fin-sheet:first-child { margin-top: 0 !important; }
  #print-area .fin-sheet > div:first-child { margin-top: 0 !important; }
  /* 表の書式(table-layout/列幅/フォント/余白/折返し)は SHEET_CSS(screen/print 共通=WYSIWYG)へ一本化。
     ここ(@media print)は印刷専用の機構(配置/シート余白リセット/改ページ/見出し繰返し)のみ。 */
  /* 行がページ境界で分断されないようにする */
  #print-area tr, #print-area td, #print-area th { break-inside: avoid; page-break-inside: avoid; }
  /* 各ページの先頭に列見出し(科目名/前月残高…)を繰り返す */
  #print-area thead { display: table-header-group; }
}
`;

// --- 行の表示/非表示設定(localStorage 永続。kind+ラベル単位) ---
const HIDDEN_KEY = 'freeeman.financial.hiddenRows.v1';
const loadHidden = (): Record<string, boolean> => {
  try { return JSON.parse(localStorage.getItem(HIDDEN_KEY) || '{}'); } catch { return {}; }
};
const saveHidden = (h: Record<string, boolean>) => {
  try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(h)); } catch { /* ignore */ }
};
const hiddenKey = (kind: string, label: string) => `${kind}::${label}`;

// --- 新規勘定科目の「確認済み」baseline(localStorage 永続。kind→確認済みラベル配列) ---
//   freee に新たに現れた勘定科目(テンプレ外=isAutoInserted)で baseline 未登録のものを「新規」と判定し
//   赤丸+行色で強調。ユーザーが「確認済みにする」と baseline に追加され強調が消える。
const ACK_KEY = 'freeeman.financial.acknowledgedLabels.v1';
const loadAck = (): Record<string, string[]> => {
  try { return JSON.parse(localStorage.getItem(ACK_KEY) || '{}'); } catch { return {}; }
};
const saveAck = (a: Record<string, string[]>) => {
  try { localStorage.setItem(ACK_KEY, JSON.stringify(a)); } catch { /* ignore */ }
};

export const FinancialStatementsPage: React.FC = () => {
  const [term, setTerm] = useState<number>(80);
  const [month, setMonth] = useState<number>(4); // 既定: 期の4ヶ月目(=1月)
  const [active, setActive] = useState<ReportKind>('bs');
  const [reports, setReports] = useState<Partial<Record<ReportKind, FinancialReport>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Record<string, boolean>>(() => loadHidden());
  const [acknowledged, setAcknowledged] = useState<Record<string, string[]>>(() => loadAck());
  const [showSettings, setShowSettings] = useState(false);
  // Phase2: 期間(複数月)モード + 全帳票一括印刷
  const [periodMode, setPeriodMode] = useState<'single' | 'range'>('single');
  const [rangeStart, setRangeStart] = useState<number>(1);
  const [rangeEnd, setRangeEnd] = useState<number>(4);
  const [printAll, setPrintAll] = useState(false);
  const [includeAdj, setIncludeAdj] = useState<boolean>(() => getIncludeAdjustment()); // 整理仕訳 含む/除く
  const [zipping, setZipping] = useState(false); // 全帳票ZIP一括DL中

  const isHidden = (label: string) => !!hidden[hiddenKey(active, label)];
  const toggleHidden = (label: string) => {
    setHidden((h) => {
      const k = hiddenKey(active, label);
      const next = { ...h };
      if (next[k]) delete next[k]; else next[k] = true;
      saveHidden(next);
      return next;
    });
  };
  const showAllForActive = () => {
    setHidden((h) => {
      const next: Record<string, boolean> = {};
      for (const k of Object.keys(h)) if (!k.startsWith(`${active}::`)) next[k] = h[k];
      saveHidden(next);
      return next;
    });
  };
  // kind 別の 非表示ラベル Set(rebuild へ渡す)
  const hiddenLabelsFor = useCallback((kind: string): Set<string> => {
    const s = new Set<string>();
    const pfx = `${kind}::`;
    for (const k of Object.keys(hidden)) if (hidden[k] && k.startsWith(pfx)) s.add(k.slice(pfx.length));
    return s;
  }, [hidden]);
  // 新規(テンプレ外=isAutoInserted かつ baseline 未確認)ラベル Set。fullReport の行から判定。
  const newLabelsFor = useCallback((kind: string, fullReport?: FinancialReport): Set<string> => {
    const s = new Set<string>();
    if (!fullReport) return s;
    const ack = new Set(acknowledged[kind] || []);
    for (const row of fullReport.rows) {
      if (row.isAutoInserted && row.label && !ack.has(row.label)) s.add(row.label);
    }
    return s;
  }, [acknowledged]);
  // 現在表示中の新規勘定科目を「確認済み」に(赤丸を消す)
  const acknowledgeNew = (kind: string, fullReport?: FinancialReport) => {
    if (!fullReport) return;
    setAcknowledged((a) => {
      const cur = new Set(a[kind] || []);
      for (const row of fullReport.rows) if (row.isAutoInserted && row.label) cur.add(row.label);
      const next = { ...a, [kind]: Array.from(cur) };
      saveAck(next);
      return next;
    });
  };

  const monthLabel = (fm: number) => {
    const c = fiscalMonthToCalendar(term, fm);
    return `${fm}月度 (${c.year}年${c.month}月)`;
  };

  const generate = useCallback(async () => {
    setLoading(true); setError(null);
    const next: Partial<Record<ReportKind, FinancialReport>> = {};
    const errs: string[] = [];
    const period: PeriodSelection = periodMode === 'range'
      ? { fiscalTerm: term, fiscalMonth: rangeEnd, mode: 'range', rangeStartMonth: rangeStart, rangeEndMonth: rangeEnd }
      : { fiscalTerm: term, fiscalMonth: month, mode: 'single' };
    for (const k of IMPLEMENTED) {
      try { next[k] = await fetchReport(k, period); }
      catch (e: any) { errs.push(`${k}: ${e.message || e}`); }
    }
    setReports(next);
    if (errs.length) setError(errs.join(' / '));
    setLoading(false);
  }, [term, month, periodMode, rangeStart, rangeEnd]);

  // 全帳票一括印刷: printAll を立てて全帳票を #print-area に描画 → 次フレームで印刷 → 解除
  useEffect(() => {
    if (!printAll) return;
    const id = window.setTimeout(() => {
      window.print();
      setPrintAll(false);
    }, 150);
    return () => window.clearTimeout(id);
  }, [printAll]);

  // 全帳票ZIP一括DL用: ファイル名の期間ラベル(暦年月)。単月=「2026年3月」/期間=「2025年10月〜2026年3月」
  const periodLabelForFile = useCallback((): string => {
    if (periodMode === 'range') {
      const s = fiscalMonthToCalendar(term, rangeStart);
      const e = fiscalMonthToCalendar(term, rangeEnd);
      return `${s.year}年${s.month}月〜${e.year}年${e.month}月`;
    }
    const c = fiscalMonthToCalendar(term, month);
    return `${c.year}年${c.month}月`;
  }, [term, month, periodMode, rangeStart, rangeEnd]);

  // 全帳票を個別PDFにして ZIP 一括DL(worker Browser Rendering が印刷同一CSSで各帳票 page.pdf 化)。
  const downloadAllZip = useCallback(async () => {
    setZipping(true); setError(null);
    try {
      const period = periodLabelForFile();
      const sp = new URLSearchParams({
        term: String(term), mode: periodMode, month: String(month),
        rangeStart: String(rangeStart), rangeEnd: String(rangeEnd), period,
      });
      // worker のヘッドレスブラウザには user の localStorage が無いため、 確認済み(ACK)と
      // 表示/非表示(HIDDEN)状態を送信し、 worker 側で描画前に localStorage へ注入する。
      // これをしないと確認済みの新規勘定科目が全て「新規(赤丸)」に戻り、 非表示行も復活する。
      // Content-Type を明示しない(=text/plain 相当)ことで CORS preflight を回避(simple request)。
      const res = await fetch(`${ZIP_API_BASE}/api/reports/financial-zip?${sp.toString()}`, {
        method: 'POST',
        body: JSON.stringify({ ack: loadAck(), hidden: loadHidden() }),
      });
      if (!res.ok) {
        let msg = `${res.status}`;
        try { const j = await res.json(); msg = j.message || j.error || msg; } catch { /* */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${term}期_${period}_決算書一式.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      setError(`全帳票ダウンロード失敗: ${e.message || e}`);
    } finally {
      setZipping(false);
    }
  }, [term, month, periodMode, rangeStart, rangeEnd, periodLabelForFile]);

  const cur = reports[active]; // 生成時の完全版(非表示なし)。設定一覧・新規判定に使用
  // 非表示を適用した表示用レポート(rebuild で小計/合計/構成比を非表示除外で再集計。再fetchなし)
  const applyHidden = (rep?: FinancialReport): FinancialReport | undefined => {
    if (!rep) return undefined;
    return rep.rebuild ? rep.rebuild(hiddenLabelsFor(rep.kind)) : rep;
  };
  const visibleReport = applyHidden(cur);
  const newLabelsActive = newLabelsFor(active, cur);
  const isLandscape = (active === 'yoy') || !!FINANCIAL_LAYOUTS[active]?.landscape;
  // Phase2 一括印刷: 生成済みの全帳票(REPORT_KINDS 順、非表示適用後)
  const allReports: FinancialReport[] = REPORT_KINDS
    .map((rk) => reports[rk.key])
    .filter((r): r is FinancialReport => !!r)
    .map((r) => applyHidden(r)!);
  const anyGenerated = allReports.length > 0;

  return (
    <div style={S.page}>
      <Container>
        <Breadcrumbs links={[{ url: '#', title: 'FREEEMAN' }, { url: '#financial', title: '決算書作成' }]} />
        <div style={{ marginTop: 16, marginBottom: 12 }}>
          <HStack gap={2} alignItems="center">
            <MdSummarize size={28} color="#2563eb" />
            <PageTitle>決算書作成</PageTitle>
          </HStack>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Note>
            freee から自動取得し、Excel テンプレートの体裁・群構成（現金計・預金計・各小計など会社独自グルーピング）で
            決算帳票を生成します（読み取りのみ）。テンプレに無い新規勘定科目は freee の決算書表示名から所属群を判定して
            自動挿入し、判定不能なものは「その他(未分類)」として明示警告します（黙って落とさない・0埋めしない）。
            会計期と月を選んで生成し、印刷ボタンでPDF保存できます。
          </Note>
        </div>

        <CardBase>
          <div style={{ padding: 16 }} className="no-print">
            {/* 1行に収める(折返し/横スクロール無し)。冗長な見出しラベル(会計期/月/期間)は省きgapを12pxに詰めて
                全ボタンを表示。各セレクタは値自体(80期/単月/○月度)で自明なのでtitle属性で補完。
                生成ボタンは固定幅(minWidth56+16pxアイコン枠)でローディング中も幅不変→#1のレイアウトシフトも再発しない。 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'nowrap' }}>
              <select value={term} onChange={(e) => setTerm(Number(e.target.value))} style={SEL} title="会計期">
                {TERMS.map((t) => (<option key={t} value={t}>{t}期</option>))}
              </select>
              <select value={periodMode} onChange={(e) => setPeriodMode(e.target.value as 'single' | 'range')} style={SEL} title="表示期間">
                <option value="single">単月</option>
                <option value="range">期間</option>
              </select>
              {periodMode === 'single' ? (
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={SEL} title="対象月">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((fm) => (
                    <option key={fm} value={fm}>{monthLabel(fm)}</option>
                  ))}
                </select>
              ) : (
                <>
                  <select value={rangeStart} onChange={(e) => setRangeStart(Number(e.target.value))} style={SEL} title="開始月">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((fm) => (
                      <option key={fm} value={fm}>{monthLabel(fm)}</option>
                    ))}
                  </select>
                  <Text>〜</Text>
                  <select value={rangeEnd} onChange={(e) => setRangeEnd(Number(e.target.value))} style={SEL} title="終了月">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((fm) => (
                      <option key={fm} value={fm}>{monthLabel(fm)}</option>
                    ))}
                  </select>
                </>
              )}
              <select value={includeAdj ? '1' : '0'} onChange={(e) => { const v = e.target.value === '1'; setIncludeAdjustment(v); setIncludeAdj(v); }} style={SEL}
                title="決算整理仕訳の扱い。決算月(9月)は減価償却費の年間精算が整理仕訳で入るため、月次比較は「除く」、決算書・年間合計は「含む」">
                <option value="1">整理仕訳 含む</option>
                <option value="0">整理仕訳 除く</option>
              </select>
              <Button appearance="primary" onClick={generate} disabled={loading}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 56, justifyContent: 'center' }}>
                  <span style={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {loading ? <InlineSpinner isLoading={true} /> : <MdRefresh size={16} />}
                  </span>
                  生成
                </span>
              </Button>
              <Button onClick={() => window.print()} disabled={!cur}>
                <MdPrint /> 印刷/PDF
              </Button>
              <Button onClick={() => setPrintAll(true)} disabled={!anyGenerated}>
                <MdPrint /> 全帳票印刷
              </Button>
              <Button onClick={downloadAllZip} disabled={!anyGenerated || zipping}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {zipping ? <InlineSpinner isLoading={true} /> : <MdDownload size={16} />}
                  </span>
                  全帳票DL
                </span>
              </Button>
              <Button onClick={() => setShowSettings((s) => !s)} disabled={!cur}>
                <MdVisibility /> 表示設定
              </Button>
            </div>

            {error && <div style={{ marginTop: 12 }}><MessageBlock success={false}>{error}</MessageBlock></div>}

            <div style={{ marginTop: 12 }}>
              <HStack gap={1}>
                {REPORT_KINDS.map((rk) => {
                  const ready = IMPLEMENTED.includes(rk.key);
                  const has = !!reports[rk.key];
                  return (
                    <button key={rk.key} data-kind={rk.key} onClick={() => setActive(rk.key)} disabled={!ready}
                      style={{
                        padding: '6px 12px', borderRadius: 4, cursor: ready ? 'pointer' : 'not-allowed',
                        border: active === rk.key ? '2px solid #2563eb' : '1px solid #d4d2d0',
                        background: active === rk.key ? '#eff6ff' : '#fff',
                        color: ready ? '#222' : '#aaa', fontSize: '0.8rem', fontWeight: active === rk.key ? 700 : 400,
                      }}>
                      {rk.label}{!ready ? '（準備中）' : has ? '' : ' …'}
                    </button>
                  );
                })}
              </HStack>
            </div>

            {showSettings && cur && (
              <div style={{ marginTop: 12, padding: 12, border: '1px solid #d4d2d0', borderRadius: 6, background: '#fafafa' }}>
                <HStack gap={2} alignItems="center" justifyContent="space-between">
                  <Text weight="bold">表示する行（チェックを外すと非表示・計算除外・印刷にも反映）</Text>
                  <HStack gap={1} alignItems="center">
                    {newLabelsActive.size > 0 && (
                      <button onClick={() => acknowledgeNew(active, cur)}
                        style={{ padding: '4px 10px', borderRadius: 4, border: `1px solid ${NEW_DOT}`, background: '#fff', color: NEW_DOT, cursor: 'pointer', fontSize: '0.8rem' }}>
                        新規 {newLabelsActive.size} 件を確認済みにする
                      </button>
                    )}
                    <button onClick={showAllForActive}
                      style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #d4d2d0', background: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>
                      この帳票をすべて表示
                    </button>
                  </HStack>
                </HStack>
                {newLabelsActive.size > 0 && (
                  <div style={{ marginTop: 6, fontSize: '0.78rem', color: NEW_DOT }}>
                    <span style={{ marginRight: 4 }}>●</span>新規勘定科目が {newLabelsActive.size} 件追加されています（行は薄赤で強調）。表示要否を設定してください。
                  </div>
                )}
                <div style={{ marginTop: 8, display: 'flex', gap: 16, alignItems: 'center', fontSize: '0.72rem', color: '#6b7280' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MdFunctions size={13} color="#1e3a8a" />総合計</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MdSubdirectoryArrowRight size={13} color="#2563eb" />小計（カテゴリ）</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MdRemove size={13} color="#9ca3af" />勘定科目</span>
                </div>
                <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', maxHeight: 320, overflowY: 'auto' }}>
                  {Array.from(new Map(cur.rows.filter((r) => !r.isBlank && r.label).map((r) => [r.label, r])).values()).map((r, i) => {
                    const isNew = newLabelsActive.has(r.label);
                    const off = isHidden(r.label);
                    // 階層: 総合計 > 小計(カテゴリ) > 明細(勘定科目)
                    const level = r.isTotal ? 'total' : r.isSubtotal ? 'subtotal' : 'detail';
                    const HierIcon = level === 'total' ? MdFunctions : level === 'subtotal' ? MdSubdirectoryArrowRight : MdRemove;
                    const iconColor = level === 'total' ? '#1e3a8a' : level === 'subtotal' ? '#2563eb' : '#9ca3af';
                    const rowBg = isNew ? NEW_FILL : level === 'total' ? '#eef2ff' : level === 'subtotal' ? '#f5f7ff' : undefined;
                    const leftBar = level === 'total' ? '3px solid #1e3a8a' : level === 'subtotal' ? '3px solid #93c5fd' : '3px solid transparent';
                    return (
                      <label key={`${r.label}-${i}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer',
                          padding: '3px 6px', paddingLeft: `${6 + r.indent * 14}px`,
                          background: rowBg, borderLeft: leftBar, borderRadius: 3 }}>
                        <input type="checkbox" checked={!off} onChange={() => toggleHidden(r.label)} />
                        {isNew && <span style={{ color: NEW_DOT, fontSize: '0.7em' }}>●</span>}
                        <HierIcon size={14} color={off ? '#cbd5e1' : iconColor} style={{ flexShrink: 0 }} />
                        <span style={{
                          fontWeight: level === 'total' ? 700 : level === 'subtotal' ? 600 : 400,
                          color: off ? '#aaa' : level === 'detail' ? '#374151' : '#111827',
                          letterSpacing: level === 'total' ? '0.02em' : undefined }}>{r.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </CardBase>

        <div id="print-area" style={{ marginTop: 16 }}>
          {loading && (
            <div style={{ padding: 32, textAlign: 'center' }} className="no-print">
              <HStack gap={1} alignItems="center" justifyContent="center">
                <InlineSpinner isLoading={true} /> <Text>freee から取得・整形中...</Text>
              </HStack>
            </div>
          )}
          {!loading && cur && (
            <>
              {printAll
                ? allReports.map((r, i) => <ReportSheet key={r.kind} r={r} pageBreak={i > 0} newLabels={newLabelsFor(r.kind, reports[r.kind])} />)
                : <ReportSheet r={visibleReport!} newLabels={newLabelsActive} />}
              {!printAll && <RobustnessNotices r={cur} />}
            </>
          )}
          {!loading && !cur && (
            <div style={{ padding: 32, textAlign: 'center', color: '#888' }} className="no-print">
              <Text>会計期と月を選んで「生成」を押してください。</Text>
            </div>
          )}
        </div>
      </Container>
      <style>{SHEET_CSS}</style>
      <style>{PRINT_CSS}</style>
      <style>{printAll
        ? `@media print {
            @page { size: A4 portrait; margin: 5mm 8mm; }
            @page landscapePg { size: A4 landscape; margin: 5mm 8mm; }
            .print-landscape { page: landscapePg; }
          }`
        : `@media print { @page { size: A4 ${isLandscape ? 'landscape' : 'portrait'}; margin: 5mm 8mm; } }`}</style>
    </div>
  );
};

export default FinancialStatementsPage;
